import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { resolveCombosFromProducts } from '../utils/comboCatalog';
import {
  mergeComboIntoCart,
  mergeProductIntoCart,
  loadCartFromStorage,
  saveCartToStorage,
  loadComboBoxVariants,
  saveComboBoxVariants,
  setComboBoxVariant,
} from '../utils/cartStorage';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [combos, setCombos] = useState([]);
  const [activeCombo, setActiveCombo] = useState(null);
  const [comboBoxVariants, setComboBoxVariants] = useState(() => loadComboBoxVariants());
  const cartItemsRef = useRef([]);

  const syncCart = useCallback((items) => {
    cartItemsRef.current = items;
    setCartItems(items);
    saveCartToStorage(items);
  }, []);

  // Load cart from localStorage (persists across sessions)
  useEffect(() => {
    const stored = loadCartFromStorage();
    cartItemsRef.current = stored;
    setCartItems(stored);
  }, []);

  // Load active combos for upsell triggers
  useEffect(() => {
    resolveCombosFromProducts().then(setCombos).catch(() => setCombos([]));
  }, []);

  const checkComboTrigger = useCallback((productId) => {
    const pid = Number(productId);
    const match = combos.find((combo) =>
      (combo.trigger_product_ids || []).includes(pid)
    );
    if (match) {
      setActiveCombo(match);
      return match;
    }
    return null;
  }, [combos]);

  const dismissComboModal = useCallback(() => setActiveCombo(null), []);

  const addToCart = useCallback(async (product, quantity = 1, customName = '') => {
    setLoading(true);
    try {
      const nextItems = mergeProductIntoCart(
        cartItemsRef.current,
        product,
        quantity,
        customName
      );
      syncCart(nextItems);
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [syncCart]);

  const addComboToCart = useCallback(async (combo, boxVariant = 'standard') => {
    setLoading(true);
    try {
      const result = mergeComboIntoCart(cartItemsRef.current, combo);
      if (result.addedCount === 0) {
        throw new Error('Bundle items could not be added. Please try again.');
      }
      const nextVariants = setComboBoxVariant(combo.slug, boxVariant);
      setComboBoxVariants(nextVariants);
      syncCart(result.items);
      return result.addedCount;
    } catch (error) {
      console.error('Error adding combo to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [syncCart]);

  const setComboVariant = useCallback((slug, boxVariant) => {
    const next = setComboBoxVariant(slug, boxVariant);
    setComboBoxVariants(next);
  }, []);

  const updateQuantity = async (itemId, quantity) => {
    if (quantity < 1) return;

    setLoading(true);
    try {
      const newItems = cartItemsRef.current.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      );
      syncCart(newItems);
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    setLoading(true);
    try {
      const newItems = cartItemsRef.current.filter((item) => item.id !== itemId);
      syncCart(newItems);
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      syncCart([]);
      saveComboBoxVariants({});
      setComboBoxVariants({});
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = parseFloat(item.product.price);
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const cartCount = getCartCount();

  const value = {
    cartItems,
    loading,
    combos,
    activeCombo,
    comboBoxVariants,
    addToCart,
    addComboToCart,
    setComboVariant,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartCount,
    cartCount,
    checkComboTrigger,
    dismissComboModal,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
